<template>
    <table
        class="forum-thread-list"
        :class="{ 'no-header': !header }">

        <thead v-if="header">
            <th class="threads-col">
                Thread
            </th>

            <th
                class="replies-col btn-wrapper"
                v-if="sortable">
                <button @click="onRepliesHeaderClick()">
                    Replies
                    <i
                        class="fa fa-caret-up"
                        v-if="sortable && sortOrder === 'postCount:asc'">
                    </i>
                    <i
                        class="fa fa-caret-down"
                        v-if="sortable && sortOrder === 'postCount:desc'">
                    </i>
                </button>
            </th>
            <th
                class="replies-col"
                v-else>
                Replies
            </th>

            <th
                class="views-col btn-wrapper"
                v-if="sortable">
                <button @click="onViewsHeaderClick()">
                    Views
                    <i
                        class="fa fa-caret-up"
                        v-if="sortable && sortOrder === 'viewCount:asc'">
                    </i>
                    <i
                        class="fa fa-caret-down"
                        v-if="sortable && sortOrder === 'viewCount:desc'">
                    </i>
                </button>
            </th>
            <th
                class="views-col"
                v-else>
                Views
            </th>

            <th
                class="activity-col btn-wrapper"
                v-if="sortable">
                <button @click="onActivityHeaderClick()">
                    Activity
                    <i
                        class="fa fa-caret-up"
                        v-if="sortable && sortOrder === 'lastPostCreatedAt:asc'">
                    </i>
                    <i
                        class="fa fa-caret-down"
                        v-if="sortable && sortOrder === 'lastPostCreatedAt:desc'">
                    </i>
                </button>
            </th>
            <th
                class="activity-col"
                v-else>
                Activity
            </th>
        </thead>

        <tbody>
            <tr
                :key="thread.id"
                v-for="thread in threads">

                <td class="threads-col">
                    <div class="thread-title">
                        <i
                            class="fa fa-thumb-tack"
                            title="Sticky"
                            v-if="thread.sticky">
                        </i>
                        <router-link :to="getThreadUrl(thread)">
                            {{ formatTitle(thread) }}
                        </router-link>
                    </div>
                    <slot
                        name="extra"
                        :thread="thread">
                        <div class="thread-category">
                            <router-link :to="getCategoryUrl(thread)">
                                {{ formatCategory(thread) }}
                            </router-link>
                        </div>
                    </slot>
                </td>

                <td
                    class="replies-col"
                    :title="getRepliesTooltip(thread)">
                    {{ formatReplies(thread) }}
                </td>

                <td
                    class="views-col"
                    :title="getViewsTooltip(thread)">
                    {{ formatViews(thread) }}
                </td>

                <td
                    class="activity-col"
                    :title="getActivityTooltip(thread)">
                    {{ formatActivity(thread) }}
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
    // ForumThreadList: a sortable list of forum threads.

    // Events:
    //  - update:sort-order

    import ForumUtils from 'src/utils/forum';

    function formatCount(count) {
        if (count < 1000) {
            return count;
        }
        if (count < 10000) {
            return `${(count / 1000).toFixed(1)}k`;
        }
        return `${(count / 1000).toFixed(0)}k`;
    }

    export default {
        props: {
            threads: {
                /**
                 * @typedef {object} thread
                 * @property {number} id
                 * @property {string} title
                 * @property {object} category
                 * @property {number} category.id
                 * @property {string} category.label
                 * @property {number} postCount
                 * @property {number} viewCount
                 * @property {object} firstPost
                 * @property {Date}   firstPost.createdAt
                 * @property {object} lastPost
                 * @property {Date}   lastPost.createdAt
                 */
                type: Array,
                required: true,
            },

            header: {
                type: Boolean,
                default: true,
            },

            sortable: {
                type: Boolean,
                default: false,
            },

            sortOrder: {
                type: String,
                default: 'default',
                validator(order) {
                    const allowedValues = [
                        'default',
                        'postCount:asc',
                        'postCount:desc',
                        'viewCount:asc',
                        'viewCount:desc',
                        'lastPostCreatedAt:asc',
                        'lastPostCreatedAt:desc',
                    ];
                    return allowedValues.includes(order);
                },
            },
        },

        methods: {
            onRepliesHeaderClick() {
                if (this.sortOrder !== 'postCount:desc') {
                    this.$emit('update:sort-order', 'postCount:desc');
                } else {
                    this.$emit('update:sort-order', 'postCount:asc');
                }
            },

            onViewsHeaderClick() {
                if (this.sortOrder !== 'viewCount:desc') {
                    this.$emit('update:sort-order', 'viewCount:desc');
                } else {
                    this.$emit('update:sort-order', 'viewCount:asc');
                }
            },

            onActivityHeaderClick() {
                if (this.sortOrder !== 'lastPostCreatedAt:desc') {
                    this.$emit('update:sort-order', 'lastPostCreatedAt:desc');
                } else {
                    this.$emit('update:sort-order', 'lastPostCreatedAt:asc');
                }
            },

            formatTitle(thread) {
                return thread.title;
            },

            formatCategory(thread) {
                return thread.category.label;
            },

            formatReplies(thread) {
                return formatCount(thread.postCount - 1);
            },

            formatViews(thread) {
                return formatCount(thread.viewCount);
            },

            formatActivity(thread) {
                return ForumUtils.formatRecentDate(thread.lastPost.createdAt);
            },

            getRepliesTooltip(thread) {
                if (thread.postCount === 1) {
                    return 'Has no replies';
                } else if (thread.postCount === 2) {
                    return 'Has 1 reply';
                } else {
                    return `Has ${thread.postCount - 1} replies`;
                }
            },

            getViewsTooltip(thread) {
                if (thread.viewCount === 1) {
                    return 'Viewed 1 time';
                } else {
                    return `Viewed ${thread.viewCount} times`;
                }
            },

            getActivityTooltip(thread) {
                const fdStr = ForumUtils.formatFullDate(
                    thread.firstPost.createdAt,
                );
                const ldStr = ForumUtils.formatFullDate(
                    thread.lastPost.createdAt,
                );
                return `First post: ${fdStr}\nLast post: ${ldStr}`;
            },

            getThreadUrl(thread) {
                const slug = ForumUtils.formatTitlePreview(thread.title);
                return {
                    name: 'forum-thread',
                    params: {
                        threadId: thread.id,
                        threadTitlePreview: slug,
                    },
                };
            },

            getCategoryUrl(thread) {
                return {
                    name: 'forum-category',
                    params: {
                        categoryId: thread.category.id,
                        categorySlug: thread.category.name,
                    },
                };
            },
        },
    }
</script>

<style lang="scss" scoped>
    table {
        width: 100%;
    }

    th {
        padding: 15px 10px;
        border-bottom: 3px solid #363636;
    }

    td {
        padding: 15px 10px;
        border-bottom: 1px solid #363636;
    }

    .no-header tr:first-child td {
        border-top: 1px solid #363636;
    }

    th, td {
        &.btn-wrapper {
            padding: 0;

            button {
                padding: 15px 10px;
                background: none;
                border: none;
                color: #a0a0a0;
                width: 100%;
                white-space: nowrap;

                &:hover {
                    background: #363636;
                }
                &:focus {
                    background: #363636;
                    outline: none;
                }

                i {
                    font-size: 16px;
                    margin-left: 3px;
                }
            }
        }
    }

    .replies-col {
        width: 80px;
        text-align: center;
    }
    .views-col {
        width: 80px;
        text-align: center;
    }
    .activity-col {
        width: 80px;
        text-align: center;
    }

    .thread-title {
        font-size: 16px;

        .fa-thumb-tack {
            color: #feca1e;
            padding-right: 2px;
        }

        a {
            color: #f0f0f0;
            &:hover {
                color: #33ccff;
            }
        }
    }

    .thread-category {
        margin-top: 2px;
    }
</style>



// WEBPACK FOOTER //
// thread-list.vue?27ac7641